import { type HTMLProps, useCallback } from 'react';

import { prefixWithContextEntryPoint } from '@atlassian/people-teams-ui';

import { useOrg } from '../../../org/hooks/use-org';
import { useSite } from '../../../site/hooks';
import { useNavigationActions } from '../../provider';
import { useAppConfigStoreState, useNavigationState } from '../../store/main';
import { type OpenPreviewPanelProps } from '../../types';
import { getAbsoluteLink, getSpaAndRelativePath, isModified } from './utils';

export type LinkProps = Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'> & {
	href: string;
};

export const useLinkProps = (): ((
	props: LinkProps & { openPreviewPanelProps?: OpenPreviewPanelProps },
) => LinkProps) => {
	const { contextSpaPaths, contextEntryPoint } = useNavigationState();
	const { push } = useNavigationActions();
	const { openLinksInNewTab, openPreviewPanel } = useAppConfigStoreState();
	const [{ orgId }] = useOrg();
	const [{ cloudId }] = useSite();

	const getLinkProps = useCallback(
		(props: LinkProps & { openPreviewPanelProps?: OpenPreviewPanelProps }): LinkProps => {
			const { href, onClick, target: inputTarget, openPreviewPanelProps, ...rest } = props;
			const { relativePath, isSPA } = getSpaAndRelativePath(href, contextSpaPaths ?? []);
			const absoluteLink = getAbsoluteLink(href, { orgId, cloudId });

			let target: string | undefined;

			if (openLinksInNewTab) {
				target = '_blank';
			} else if (inputTarget) {
				target = inputTarget;
			} else if (isSPA) {
				target = undefined;
			}

			const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
				if (onClick) {
					onClick(event);
				}

				if (openPreviewPanel && openPreviewPanelProps) {
					try {
						openPreviewPanel(openPreviewPanelProps);
						event.preventDefault();
						return;
					} catch (error) {}
				}
				// if the event is modified, we want to let the browser handle the link logic
				if (openLinksInNewTab && !isModified(event)) {
					event.preventDefault();
					window.open(absoluteLink, '_blank', 'noopener, noreferrer');
					return;
				}

				if (
					isSPA && // only use push for links that work for SPA transitions
					!event.defaultPrevented && // set when onClick called preventDefault
					event.button === 0 && // only use push for left mouse button clicks
					(!target || target === '_self') && // let browser handle "_blank" etc
					!isModified(event) // let browser handle clicks with modifier
				) {
					event.preventDefault();
					push(relativePath);
				}
			};

			return {
				...rest,
				href: openLinksInNewTab
					? absoluteLink
					: prefixWithContextEntryPoint(href, contextEntryPoint),
				target,
				...(!isSPA || target === '_blank' || openLinksInNewTab
					? { rel: 'noopener noreferrer' }
					: {}),
				onClick: handleClick,
			};
		},
		[contextSpaPaths, contextEntryPoint, openPreviewPanel, openLinksInNewTab, orgId, cloudId, push],
	);

	return getLinkProps;
};
